
import { createContext, useState, useContext, useEffect, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  isLoading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Defense-in-depth: ensure any active session has an active subscription.
  // Only runs on a fresh SIGNED_IN — NOT on token refreshes (those happen
  // every hour and on tab focus, and a transient edge-function blip there
  // would falsely sign the user out).
  const verifySubscription = async (currentSession: Session | null) => {
    if (!currentSession?.user) return;
    // Skip on auth page itself to avoid loops while user is actively signing up/in
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/auth")) return;
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) return; // fail open on network errors so we don't lock users out
      if (!data) return; // fail open on empty response
      // Only sign out when the server explicitly says not subscribed
      if (data.subscribed === false) {
        await supabase.auth.signOut();
        if (typeof window !== "undefined") {
          window.location.href = "/auth?tab=signin&needs_subscription=1";
        }
      }
    } catch (e) {
      console.error("Subscription verification failed", e);
      // fail open
    }
  };

  useEffect(() => {
    // Set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setIsLoading(false);
        // Only verify on a fresh sign-in. TOKEN_REFRESHED fires constantly
        // (hourly + on tab focus) and must NOT trigger sign-out.
        if (event === "SIGNED_IN") {
          setTimeout(() => verifySubscription(currentSession), 0);
        }
      }
    );

    // Check for the initial session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setIsLoading(false);
      // Do NOT re-verify on every page load — only on a real fresh sign-in.
    });


    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    if (typeof window !== "undefined") {
      window.location.href = "/";
    }
  };

  const value = {
    session,
    user,
    isLoading,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
